import React, { useEffect, useState } from "react";
import * as echarts from "echarts";
import { DatePicker, Space, Card, Row, Col, Button, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/en";
import useApiCall from "../common/api-call";
import { getAutomationStatistics } from "./statistics-service";

dayjs.locale("en");

const AutomationStatisticsView = () => {
  const { apiCall } = useApiCall();
  const [filteredData, setFilteredData] = useState([]);

  // Initialize dates
  const today = dayjs();
  const thirtyDaysAgo = dayjs().subtract(30, "day");

  const [startDate, setStartDate] = useState(thirtyDaysAgo.format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(today.format("YYYY-MM-DD"));
  const [startDateValue, setStartDateValue] = useState(thirtyDaysAgo);
  const [endDateValue, setEndDateValue] = useState(today);

  const fetchStatisticsData = async (start = startDate, end = endDate) => {
    try {
      const response = await getAutomationStatistics(apiCall, start, end);

      // Handle different possible response structures
      if (
        response &&
        response.statistics &&
        Array.isArray(response.statistics)
      ) {
        setFilteredData(response.statistics);
      } else if (response && Array.isArray(response)) {
        // Fallback for direct array response
        setFilteredData(response);
      } else {
        console.log("Unexpected response structure:", response);
        setFilteredData([]);
      }
    } catch (error) {
      console.error("Error fetching automation statistics:", error);
      setFilteredData([]);
    }
  };

  useEffect(() => {
    fetchStatisticsData();
  }, []);

  const handleFetchData = () => {
    fetchStatisticsData(startDate, endDate);
  };

  // Calculate number of days between start and end date
  const calculateDaysBetween = (start, end) => {
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const timeDiff = endDateObj.getTime() - startDateObj.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates
  };

  const daysBetween = calculateDaysBetween(startDate, endDate);

  const dayData = Array.from({ length: daysBetween }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return {
      day: date.toISOString().split("T")[0],
      // Daily recurring data
      dailyRecurringCreateCount: 0,
      dailyRecurringDeleteCount: 0,
      dailyRecurringEnableCount: 0,
      dailyRecurringDisableCount: 0,
      // Daily temp task data
      dailyTempTaskCreateCount: 0,
      dailyTempTaskDeleteCount: 0,
      dailyTempTaskRunCount: 0,
      // Daily perm task data
      dailyPermTaskCreateCount: 0,
      dailyPermTaskDeleteCount: 0,
      dailyPermTaskRunCount: 0,
      // Accumulated data
      accuTaskCount: 0,
      accuTempTaskCount: 0,
      accuTwinCount: 0,
      accuRecurringCount: 0,
      accuActiveRecurringCount: 0,
    };
  });

  filteredData?.forEach(item => {
    const itemDate = item.date || "";
    const dateIndex = dayData.findIndex(day => day.day === itemDate);
    if (dateIndex !== -1) {
      // Daily recurring data
      dayData[dateIndex].dailyRecurringCreateCount +=
        item.dailyRecurringCreateCount || 0;
      dayData[dateIndex].dailyRecurringDeleteCount +=
        item.dailyRecurringDeleteCount || 0;
      dayData[dateIndex].dailyRecurringEnableCount +=
        item.dailyRecurringEnableCount || 0;
      dayData[dateIndex].dailyRecurringDisableCount +=
        item.dailyRecurringDisableCount || 0;
      // Daily temp task data
      dayData[dateIndex].dailyTempTaskCreateCount +=
        item.dailyTempTaskCreateCount || 0;
      dayData[dateIndex].dailyTempTaskDeleteCount +=
        item.dailyTempTaskDeleteCount || 0;
      dayData[dateIndex].dailyTempTaskRunCount +=
        item.dailyTempTaskRunCount || 0;
      // Daily perm task data
      dayData[dateIndex].dailyPermTaskCreateCount +=
        item.dailyPermTaskCreateCount || 0;
      dayData[dateIndex].dailyPermTaskDeleteCount +=
        item.dailyPermTaskDeleteCount || 0;
      dayData[dateIndex].dailyPermTaskRunCount +=
        item.dailyPermTaskRunCount || 0;
      // Accumulated data
      dayData[dateIndex].accuTaskCount = item.accuTaskCount || 0;
      dayData[dateIndex].accuTempTaskCount = item.accuTempTaskCount || 0;
      dayData[dateIndex].accuTwinCount = item.accuTwinCount || 0;
      dayData[dateIndex].accuRecurringCount = item.accuRecurringCount || 0;
      dayData[dateIndex].accuActiveRecurringCount =
        item.accuActiveRecurringCount || 0;
    }
  });

  // Chart options for Daily Recurring Tasks
  const getDailyRecurringChartOption = () => {
    const maxValue = Math.max(
      ...dayData.map(item =>
        Math.max(
          item.dailyRecurringCreateCount,
          item.dailyRecurringDeleteCount,
          item.dailyRecurringEnableCount,
          item.dailyRecurringDisableCount,
        ),
      ),
      0,
    );
    const yAxisMax = maxValue > 0 ? Math.ceil(maxValue * 1.1) : 10;

    return {
      title: {
        text: "Daily Recurring Tasks",
        left: "center",
        textStyle: {
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
        formatter: params => {
          const { name } = params[0];
          let tooltipContent = `Date: ${name}<br />`;
          params.forEach(param => {
            tooltipContent += `${param.seriesName}: ${param.value}<br />`;
          });
          return tooltipContent;
        },
      },
      legend: {
        data: ["Create", "Delete", "Enable", "Disable"],
        top: "12%",
      },
      grid: {
        top: "30%",
        left: "15%",
        right: "10%",
        bottom: "15%",
      },
      xAxis: {
        type: "category",
        data: dayData.map(item => item.day),
        axisLabel: {
          interval: Math.max(1, Math.floor(daysBetween / 10)),
          rotate: 45,
          margin: 10,
          formatter: value => dayjs(value).format("MM-DD"),
          showMaxLabel: true,
        },
        axisTick: {
          alignWithLabel: true,
          interval: Math.max(1, Math.floor(daysBetween / 15)),
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: yAxisMax,
        axisLabel: {
          formatter: value => Math.round(value).toLocaleString(),
        },
      },
      series: [
        {
          name: "Create",
          type: "line",
          data: dayData.map(item => item.dailyRecurringCreateCount),
          itemStyle: { color: "#4caf50" },
        },
        {
          name: "Delete",
          type: "line",
          data: dayData.map(item => item.dailyRecurringDeleteCount),
          itemStyle: { color: "#f44336" },
        },
        {
          name: "Enable",
          type: "line",
          data: dayData.map(item => item.dailyRecurringEnableCount),
          itemStyle: { color: "#2196f3" },
        },
        {
          name: "Disable",
          type: "line",
          data: dayData.map(item => item.dailyRecurringDisableCount),
          itemStyle: { color: "#ff9800" },
        },
      ],
    };
  };

  // Chart options for Daily Temp Tasks
  const getDailyTempTaskChartOption = () => {
    const maxValue = Math.max(
      ...dayData.map(item =>
        Math.max(
          item.dailyTempTaskCreateCount,
          item.dailyTempTaskDeleteCount,
          item.dailyTempTaskRunCount,
        ),
      ),
      0,
    );
    const yAxisMax = maxValue > 0 ? Math.ceil(maxValue * 1.1) : 10;

    return {
      title: {
        text: "Daily Temporary Tasks",
        left: "center",
        textStyle: {
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
        formatter: params => {
          const { name } = params[0];
          let tooltipContent = `Date: ${name}<br />`;
          params.forEach(param => {
            tooltipContent += `${param.seriesName}: ${param.value}<br />`;
          });
          return tooltipContent;
        },
      },
      legend: {
        data: ["Create", "Delete", "Run"],
        top: "12%",
      },
      grid: {
        top: "30%",
        left: "15%",
        right: "10%",
        bottom: "15%",
      },
      xAxis: {
        type: "category",
        data: dayData.map(item => item.day),
        axisLabel: {
          interval: Math.max(1, Math.floor(daysBetween / 10)),
          rotate: 45,
          margin: 10,
          formatter: value => dayjs(value).format("MM-DD"),
          showMaxLabel: true,
        },
        axisTick: {
          alignWithLabel: true,
          interval: Math.max(1, Math.floor(daysBetween / 15)),
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: yAxisMax,
        axisLabel: {
          formatter: value => Math.round(value).toLocaleString(),
        },
      },
      series: [
        {
          name: "Create",
          type: "line",
          data: dayData.map(item => item.dailyTempTaskCreateCount),
          itemStyle: { color: "#9c27b0" },
        },
        {
          name: "Delete",
          type: "line",
          data: dayData.map(item => item.dailyTempTaskDeleteCount),
          itemStyle: { color: "#f44336" },
        },
        {
          name: "Run",
          type: "line",
          data: dayData.map(item => item.dailyTempTaskRunCount),
          itemStyle: { color: "#00bcd4" },
        },
      ],
    };
  };

  // Chart options for Daily Permanent Tasks
  const getDailyPermTaskChartOption = () => {
    const maxValue = Math.max(
      ...dayData.map(item =>
        Math.max(
          item.dailyPermTaskCreateCount,
          item.dailyPermTaskDeleteCount,
          item.dailyPermTaskRunCount,
        ),
      ),
      0,
    );
    const yAxisMax = maxValue > 0 ? Math.ceil(maxValue * 1.1) : 10;

    return {
      title: {
        text: "Daily Permanent Tasks",
        left: "center",
        textStyle: {
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
        formatter: params => {
          const { name } = params[0];
          let tooltipContent = `Date: ${name}<br />`;
          params.forEach(param => {
            tooltipContent += `${param.seriesName}: ${param.value}<br />`;
          });
          return tooltipContent;
        },
      },
      legend: {
        data: ["Create", "Delete", "Run"],
        top: "12%",
      },
      grid: {
        top: "30%",
        left: "15%",
        right: "10%",
        bottom: "15%",
      },
      xAxis: {
        type: "category",
        data: dayData.map(item => item.day),
        axisLabel: {
          interval: Math.max(1, Math.floor(daysBetween / 10)),
          rotate: 45,
          margin: 10,
          formatter: value => dayjs(value).format("MM-DD"),
          showMaxLabel: true,
        },
        axisTick: {
          alignWithLabel: true,
          interval: Math.max(1, Math.floor(daysBetween / 15)),
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: yAxisMax,
        axisLabel: {
          formatter: value => Math.round(value).toLocaleString(),
        },
      },
      series: [
        {
          name: "Create",
          type: "line",
          data: dayData.map(item => item.dailyPermTaskCreateCount),
          itemStyle: { color: "#8bc34a" },
        },
        {
          name: "Delete",
          type: "line",
          data: dayData.map(item => item.dailyPermTaskDeleteCount),
          itemStyle: { color: "#f44336" },
        },
        {
          name: "Run",
          type: "line",
          data: dayData.map(item => item.dailyPermTaskRunCount),
          itemStyle: { color: "#607d8b" },
        },
      ],
    };
  };

  // Chart options for Accumulated Data
  const getAccumulatedChartOption = () => {
    const maxValue = Math.max(
      ...dayData.map(item =>
        Math.max(
          item.accuTaskCount,
          item.accuTempTaskCount,
          item.accuTwinCount,
          item.accuRecurringCount,
          item.accuActiveRecurringCount,
        ),
      ),
      0,
    );
    const yAxisMax = maxValue > 0 ? Math.ceil(maxValue * 1.1) : 100;

    return {
      title: {
        text: "Accumulated Counts",
        left: "center",
        textStyle: {
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
        formatter: params => {
          const { name } = params[0];
          let tooltipContent = `Date: ${name}<br />`;
          params.forEach(param => {
            tooltipContent += `${
              param.seriesName
            }: ${param.value.toLocaleString()}<br />`;
          });
          return tooltipContent;
        },
      },
      legend: {
        data: [
          "Total Tasks",
          "Temp Tasks",
          "Twins",
          "Recurring",
          "Active Recurring",
        ],
        top: "12%",
      },
      grid: {
        top: "30%",
        left: "15%",
        right: "10%",
        bottom: "15%",
      },
      xAxis: {
        type: "category",
        data: dayData.map(item => item.day),
        axisLabel: {
          interval: Math.max(1, Math.floor(daysBetween / 10)),
          rotate: 45,
          margin: 10,
          formatter: value => dayjs(value).format("MM-DD"),
          showMaxLabel: true,
        },
        axisTick: {
          alignWithLabel: true,
          interval: Math.max(1, Math.floor(daysBetween / 15)),
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: yAxisMax,
        axisLabel: {
          formatter: value => Math.round(value).toLocaleString(),
        },
      },
      series: [
        {
          name: "Total Tasks",
          type: "line",
          data: dayData.map(item => item.accuTaskCount),
          itemStyle: { color: "#3f51b5" },
          lineStyle: { width: 3 },
        },
        {
          name: "Temp Tasks",
          type: "line",
          data: dayData.map(item => item.accuTempTaskCount),
          itemStyle: { color: "#e91e63" },
        },
        {
          name: "Twins",
          type: "line",
          data: dayData.map(item => item.accuTwinCount),
          itemStyle: { color: "#ff5722" },
        },
        {
          name: "Recurring",
          type: "line",
          data: dayData.map(item => item.accuRecurringCount),
          itemStyle: { color: "#795548" },
        },
        {
          name: "Active Recurring",
          type: "line",
          data: dayData.map(item => item.accuActiveRecurringCount),
          itemStyle: { color: "#009688" },
        },
      ],
    };
  };

  useEffect(() => {
    // Initialize charts
    const dailyRecurringChartDom = document.getElementById(
      "daily-recurring-chart",
    );
    const dailyTempTaskChartDom = document.getElementById(
      "daily-temp-task-chart",
    );
    const dailyPermTaskChartDom = document.getElementById(
      "daily-perm-task-chart",
    );
    const accumulatedChartDom = document.getElementById("accumulated-chart");

    if (
      dailyRecurringChartDom &&
      dailyTempTaskChartDom &&
      dailyPermTaskChartDom &&
      accumulatedChartDom
    ) {
      const dailyRecurringChart = echarts.init(dailyRecurringChartDom);
      const dailyTempTaskChart = echarts.init(dailyTempTaskChartDom);
      const dailyPermTaskChart = echarts.init(dailyPermTaskChartDom);
      const accumulatedChart = echarts.init(accumulatedChartDom);

      dailyRecurringChart.setOption(getDailyRecurringChartOption());
      dailyTempTaskChart.setOption(getDailyTempTaskChartOption());
      dailyPermTaskChart.setOption(getDailyPermTaskChartOption());
      accumulatedChart.setOption(getAccumulatedChartOption());

      const handleResize = () => {
        dailyRecurringChart.resize();
        dailyTempTaskChart.resize();
        dailyPermTaskChart.resize();
        accumulatedChart.resize();
      };

      window.addEventListener("resize", handleResize);

      return () => {
        dailyRecurringChart.dispose();
        dailyTempTaskChart.dispose();
        dailyPermTaskChart.dispose();
        accumulatedChart.dispose();
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [filteredData, startDate, endDate]);

  // Calculate totals for summary cards
  const totalDailyRecurringCreate = dayData.reduce(
    (sum, item) => sum + item.dailyRecurringCreateCount,
    0,
  );
  const totalDailyTempTaskRun = dayData.reduce(
    (sum, item) => sum + item.dailyTempTaskRunCount,
    0,
  );
  const totalDailyPermTaskRun = dayData.reduce(
    (sum, item) => sum + item.dailyPermTaskRunCount,
    0,
  );
  const totalDailyTaskRuns = totalDailyTempTaskRun + totalDailyPermTaskRun;

  // Get the latest accumulated counts (from the most recent day with data)
  const latestDayWithData =
    [...dayData].reverse().find(day => day.accuTaskCount > 0) ||
    dayData[dayData.length - 1];
  const currentTotalTasks = latestDayWithData.accuTaskCount;
  const currentActiveRecurring = latestDayWithData.accuActiveRecurringCount;

  return (
    <div style={{ flexGrow: 1, height: "100%", margin: 16, paddingBottom: 80 }}>
      {/* Date Selection Controls */}
      <Card
        style={{
          marginBottom: 24,
          border: "1px solid #e0e0e0",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Typography.Title level={5} style={{ marginBottom: 16, color: "#333" }}>
          Date Range Selection
        </Typography.Title>
        <Row gutter={16} align="bottom">
          <Col xs={24} sm={10}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                Start Date
              </Typography.Text>
              <DatePicker
                value={startDateValue}
                onChange={(date) => {
                  setStartDateValue(date);
                  setStartDate(date ? date.format("YYYY-MM-DD") : "");
                }}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
            />
            </Space>
          </Col>
          <Col xs={24} sm={10}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                End Date
              </Typography.Text>
              <DatePicker
                value={endDateValue}
                onChange={(date) => {
                  setEndDateValue(date);
                  setEndDate(date ? date.format("YYYY-MM-DD") : "");
                }}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
            />
            </Space>
          </Col>
          <Col xs={24} sm={4}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleFetchData}
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
              }}
            />
          </Col>
        </Row>
      </Card>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24, justifyContent: "center" }}>
        <Col xs={12} sm={6}>
          <Card style={{ textAlign: "center", maxWidth: 250, minWidth: 150 }}>
            <div style={{ fontSize: "14px", fontWeight: "bold", color: "#333", marginBottom: 8 }}>
            Total Tasks
            </div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#3f51b5" }}>
            {currentTotalTasks.toLocaleString()}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card style={{ textAlign: "center", maxWidth: 250, minWidth: 150 }}>
            <div style={{ fontSize: "14px", fontWeight: "bold", color: "#333", marginBottom: 8 }}>
            Active Recurring
            </div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#009688" }}>
            {currentActiveRecurring.toLocaleString()}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card style={{ textAlign: "center", maxWidth: 250, minWidth: 150 }}>
            <div style={{ fontSize: "14px", fontWeight: "bold", color: "#333", marginBottom: 8 }}>
            Recurring Created
            </div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#4caf50" }}>
            {totalDailyRecurringCreate.toLocaleString()}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card style={{ textAlign: "center", maxWidth: 250, minWidth: 150 }}>
            <div style={{ fontSize: "14px", fontWeight: "bold", color: "#333", marginBottom: 8 }}>
            Total Task Runs
            </div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#2196f3" }}>
            {totalDailyTaskRuns.toLocaleString()}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Charts in Column Layout */}
      <Row gutter={24}>
        <Col xs={24}>
          <div style={{ width: "100%", height: "450px" }} className="chart-container">
            <div
              id="daily-recurring-chart"
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        </Col>
        <Col xs={24}>
          <div style={{ width: "100%", height: "450px" }} className="chart-container">
            <div
              id="daily-temp-task-chart"
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        </Col>
        <Col xs={24}>
          <div style={{ width: "100%", height: "450px" }} className="chart-container">
            <div
              id="daily-perm-task-chart"
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        </Col>
        <Col xs={24}>
          <div style={{ width: "100%", height: "450px" }} className="chart-container">
            <div
              id="accumulated-chart"
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default AutomationStatisticsView;
